import React from 'react';
import { Check, Download, FileText, Pencil, Trash2, X } from 'lucide-react';
import { InlineLoadingState } from '../../../components/LoadingSpinner';
import { AppSelect } from '../../../components/AppSelect';
import {
  getBusinessDocumentVisualStyle,
  isBusinessDocumentPdf,
} from '../utils/documentVisuals';

export type BusinessDocumentItem = {
  id: string;
  folder_id: string | null;
  display_name: string;
  filename: string;
  mime_type: string | null;
  size_bytes: number;
  created_at: string;
  updated_at: string;
  folder_name?: string | null;
};

export type BusinessDocumentFolderOption = {
  id: string;
  name: string;
};

type BusinessDocumentsListProps = {
  documents: BusinessDocumentItem[];
  folders: BusinessDocumentFolderOption[];
  loading: boolean;
  error: string | null;
  editingDocumentId: string | null;
  editName: string;
  editFolderId: string;
  setEditName: (value: string) => void;
  setEditFolderId: (value: string) => void;
  onStartEdit: (doc: BusinessDocumentItem) => void;
  onSaveEdit: (doc: BusinessDocumentItem) => void;
  onCancelEdit: () => void;
  savingEditDocumentId?: string | null;
  onDelete: (doc: BusinessDocumentItem) => void;
  deletingDocumentId?: string | null;
  onDownload: (doc: BusinessDocumentItem) => void;
  downloadingDocumentId?: string | null;
  onPreview?: (doc: BusinessDocumentItem) => void;
  previewingDocumentId?: string | null;
};

function formatDate(iso?: string): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString('de-DE');
}

function formatSize(size?: number): string {
  if (!size || !Number.isFinite(size)) return '—';
  if (size < 1024) return `${size} B`;
  if (size < 1024 * 1024) return `${(size / 1024).toFixed(1)} KB`;
  return `${(size / (1024 * 1024)).toFixed(1)} MB`;
}

export function BusinessDocumentsList({
  documents,
  folders,
  loading,
  error,
  editingDocumentId,
  editName,
  editFolderId,
  setEditName,
  setEditFolderId,
  onStartEdit,
  onSaveEdit,
  onCancelEdit,
  savingEditDocumentId = null,
  onDelete,
  deletingDocumentId = null,
  onDownload,
  downloadingDocumentId = null,
  onPreview,
  previewingDocumentId = null,
}: BusinessDocumentsListProps) {
  const folderOptions = React.useMemo(
    () => [{ value: '', label: 'Kein Ordner' }, ...folders.map((f) => ({ value: f.id, label: f.name }))],
    [folders],
  );

  const renderDocumentName = (doc: BusinessDocumentItem, className = '') => {
    const name = doc.display_name || doc.filename;
    const isPdf = isBusinessDocumentPdf(doc.filename, doc.mime_type);
    if (isPdf && onPreview) {
      return (
        <button
          type="button"
          onClick={() => onPreview(doc)}
          disabled={previewingDocumentId === doc.id}
          className={`font-medium text-pipedrive-blue hover:underline truncate text-left disabled:opacity-50 ${className}`}
          title="PDF-Vorschau öffnen"
        >
          {previewingDocumentId === doc.id ? 'Lädt Vorschau…' : name}
        </button>
      );
    }
    return <p className={`font-medium text-app-text truncate ${className}`}>{name}</p>;
  };

  const renderDocumentIcon = (doc: BusinessDocumentItem, size: 'sm' | 'md' = 'md') => {
    const style = getBusinessDocumentVisualStyle(doc.filename, doc.mime_type);
    const box = size === 'sm' ? 'w-7 h-7' : 'w-8 h-8';
    const icon = size === 'sm' ? 'w-3.5 h-3.5' : 'w-4 h-4';
    return (
      <div className={`${box} rounded-md ${style.bg} ${style.text} flex items-center justify-center shrink-0`}>
        <FileText className={icon} />
      </div>
    );
  };

  if (loading) {
    return (
      <div className="rounded-xl border border-app-border bg-app-surface p-8">
        <InlineLoadingState label="Lädt Dokumente..." />
      </div>
    );
  }

  if (error) {
    return (
      <div className="rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-700">
        {error}
      </div>
    );
  }

  if (!documents.length) {
    return (
      <div className="rounded-xl border border-app-border bg-app-surface p-8 text-center text-app-text-subtle text-sm">
        Noch keine Dokumente vorhanden.
      </div>
    );
  }

  return (
    <div className="rounded-xl border border-app-border bg-app-surface overflow-visible">
      <div className="md:hidden divide-y divide-app-border">
        {documents.map((doc) => {
          const isEditing = editingDocumentId === doc.id;
          const isSaving = savingEditDocumentId === doc.id;
          return (
            <div key={doc.id}>
              <div className="px-4 py-3 flex items-center gap-3">
                {renderDocumentIcon(doc)}
                <div className="flex-1 min-w-0">
                  {renderDocumentName(doc, 'text-sm')}
                  <p className="text-xs text-app-text-subtle truncate">
                    {doc.folder_name || 'Kein Ordner'} · {formatSize(doc.size_bytes)}
                  </p>
                </div>
                <div className="flex items-center gap-1 shrink-0">
                  {isEditing ? (
                    <>
                      <button
                        type="button"
                        onClick={() => onSaveEdit(doc)}
                        disabled={isSaving}
                        className="p-1.5 rounded hover:bg-green-50 disabled:opacity-50"
                        aria-label="Speichern"
                      >
                        <Check className="w-4 h-4 text-green-600" />
                      </button>
                      <button
                        type="button"
                        onClick={onCancelEdit}
                        disabled={isSaving}
                        className="p-1.5 rounded hover:bg-app-hover"
                        aria-label="Abbrechen"
                      >
                        <X className="w-4 h-4 text-app-text-subtle" />
                      </button>
                    </>
                  ) : (
                    <button
                      type="button"
                      onClick={() => onStartEdit(doc)}
                      className="p-1.5 rounded hover:bg-app-hover"
                      aria-label="Bearbeiten"
                    >
                      <Pencil className="w-4 h-4 text-app-text-subtle" />
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={() => onDownload(doc)}
                    disabled={downloadingDocumentId === doc.id}
                    className="p-1.5 rounded hover:bg-sky-50 disabled:opacity-50"
                    aria-label="Herunterladen"
                    title="Herunterladen"
                  >
                    <Download className="w-4 h-4 text-blue-600" />
                  </button>
                  <button
                    type="button"
                    onClick={() => onDelete(doc)}
                    disabled={deletingDocumentId === doc.id}
                    className="p-1.5 rounded hover:bg-red-50 disabled:opacity-50"
                    aria-label="Löschen"
                  >
                    <Trash2 className="w-4 h-4 text-red-500" />
                  </button>
                </div>
              </div>
              {isEditing && (
                <div className="px-4 pb-3 space-y-2 bg-app-surface-muted border-t border-app-border">
                  <input
                    value={editName}
                    onChange={(e) => setEditName(e.target.value)}
                    className="w-full border border-app-border rounded-lg px-3 py-2 text-sm text-app-text focus:ring-2 focus:ring-pipedrive-blue/20 outline-none"
                    placeholder="Dokumentname"
                  />
                  <AppSelect
                    value={editFolderId}
                    onChange={(v) => setEditFolderId(v)}
                    options={folderOptions}
                    size="sm"
                    className="w-full"
                  />
                </div>
              )}
            </div>
          );
        })}
      </div>

      <table className="hidden md:table min-w-full text-sm">
        <thead>
          <tr>
            <th scope="col" className="sticky top-0 z-10 border-b border-app-border bg-app-surface-muted px-3 py-2.5 text-left text-xs font-semibold text-app-text-subtle">
              Dokument
            </th>
            <th scope="col" className="sticky top-0 z-10 border-b border-app-border bg-app-surface-muted px-3 py-2.5 text-left text-xs font-semibold text-app-text-subtle">
              Ordner
            </th>
            <th scope="col" className="sticky top-0 z-10 border-b border-app-border bg-app-surface-muted px-3 py-2.5 text-left text-xs font-semibold text-app-text-subtle">
              Größe
            </th>
            <th scope="col" className="sticky top-0 z-10 border-b border-app-border bg-app-surface-muted px-3 py-2.5 text-left text-xs font-semibold text-app-text-subtle">
              Erstellt
            </th>
            <th
              scope="col"
              className="sticky top-0 z-10 w-24 border-b border-app-border bg-app-surface-muted px-3 py-2.5 text-right text-xs font-semibold text-app-text-subtle"
            />
          </tr>
        </thead>
        <tbody>
          {documents.map((doc) => (
            <tr key={doc.id} className="border-b border-app-border hover:bg-app-hover">
              <td className="px-3 py-2">
                <div className="flex items-center gap-2.5 min-w-0">
                  {renderDocumentIcon(doc, 'sm')}
                  <div className="min-w-0">
                    {editingDocumentId === doc.id ? (
                      <input
                        value={editName}
                        onChange={(e) => setEditName(e.target.value)}
                        className="w-full min-w-[220px] border border-app-border rounded px-2 py-1 text-sm text-app-text"
                        placeholder="Dokumentname"
                      />
                    ) : (
                      renderDocumentName(doc)
                    )}
                  </div>
                </div>
              </td>
              <td className="px-3 py-2 text-app-text-muted">
                {editingDocumentId === doc.id ? (
                  <div className="min-w-[180px]">
                    <AppSelect
                      value={editFolderId}
                      onChange={(value) => setEditFolderId(value)}
                      options={folderOptions}
                      size="sm"
                    />
                  </div>
                ) : (
                  doc.folder_name || '—'
                )}
              </td>
              <td className="px-3 py-2 text-app-text-muted">{formatSize(doc.size_bytes)}</td>
              <td className="px-3 py-2 text-app-text-muted">{formatDate(doc.created_at)}</td>
              <td className="px-3 py-2">
                <div className="flex items-center justify-end gap-1">
                  {editingDocumentId === doc.id ? (
                    <>
                      <button
                        type="button"
                        onClick={() => onSaveEdit(doc)}
                        className="p-1 rounded hover:bg-green-50 disabled:opacity-50"
                        title="Speichern"
                        aria-label="Speichern"
                        disabled={savingEditDocumentId === doc.id}
                      >
                        <Check className="w-4 h-4 text-green-600" />
                      </button>
                      <button
                        type="button"
                        onClick={onCancelEdit}
                        className="p-1 rounded hover:bg-app-hover"
                        title="Abbrechen"
                        aria-label="Abbrechen"
                        disabled={savingEditDocumentId === doc.id}
                      >
                        <X className="w-4 h-4 text-app-text-subtle" />
                      </button>
                    </>
                  ) : (
                    <button
                      type="button"
                      onClick={() => onStartEdit(doc)}
                      className="p-1 rounded hover:bg-app-hover"
                      title="Dokument bearbeiten"
                      aria-label="Dokument bearbeiten"
                    >
                      <Pencil className="w-4 h-4 text-app-text-subtle" />
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={() => onDownload(doc)}
                    className="p-1 rounded hover:bg-sky-50 disabled:opacity-50"
                    title="Dokument herunterladen"
                    aria-label="Dokument herunterladen"
                    disabled={downloadingDocumentId === doc.id}
                  >
                    <Download className="w-4 h-4 text-blue-600" />
                  </button>
                  <button
                    type="button"
                    onClick={() => onDelete(doc)}
                    className="p-1 rounded hover:bg-red-50 disabled:opacity-50"
                    title="Dokument löschen"
                    aria-label="Dokument löschen"
                    disabled={deletingDocumentId === doc.id}
                  >
                    <Trash2 className="w-4 h-4 text-red-500" />
                  </button>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
